import React, {
  Suspense, useContext, useEffect, useRef, useState
} from 'react';
import proptypes from 'prop-types';
import classNames from 'classnames';
import { Carousel, Image } from '@thd-olt-component-react/core-ui';
import { ExperienceContext, useConfigService, useStoreId } from '@thd-nucleus/experience-context';
import { useDataModel, useLazyDataModel } from '@thd-nucleus/data-sources';
import { PaintSwatches } from '@one-thd/sui-icons';
import { useLifeCycleEventBus } from '@thd-olt-functional/utils';
import { useAvataar } from '../../../hooks/useAvataar';
import {
  ARIcon, BackgroundCircleIcon, HotspotTagIcon, PlayIcon, ThreeSixtyIcon
} from '../Icons';
import { componentReady, mediaClick, thumbnailClick } from '../../../publisher';
import { MediaGalleryLoader } from '../MediaGalleryLoader/MediaGalleryLoader';
import Helper from '../../Helper';
import './media-gallery-carousel.style.scss';
import { dataModel } from '../../dataModel';
import FloatingActionButton from '../../common/FloatingActionButton';

const {
  bool, string, oneOf, func, shape
} = proptypes;

const MediaGalleryOverlay = React.lazy(() => import(
  /* webpackChunkName: "media-gallery-overlay" */'../MediaGalleryOverlay'
));
const MediaGalleryOverlayMobile = React.lazy(() => import(
  /* webpackChunkName: "media-gallery-overlay-mobile" */'../MediaGalleryOverlayMobile'
));
const FilmstripMobile = React.lazy(() => import(
  /* webpackChunkName: "media-gallery-filmstrip-mobile" */'../FilmstripMobile'
));

export const MediaGalleryCarousel = ({
  itemId,
  hover = false,
  overlayType = 'default',
  showDynamicPagination = false,
  firstItemFetchPriority = 'auto',
  showLoader = false,
  configId = undefined,
  onMediaClick = null,
  overrideData = null,
  pageType = '',
  skipReviewPhotos = false
}) => {
  const experienceContext = useContext(ExperienceContext);
  const storeId = useStoreId();
  const carouselWrapperRef = useRef(null);
  const { channel } = experienceContext;
  const isMobile = channel === 'mobile';

  const showFloatingActionButton = useConfigService('fs:is-floating-action-button-enabled');

  const [mainAsset, setMainAsset] = useState(null);
  const { showAvataarDefault, showAvataarAR, setShowAvataarAR } = useAvataar(itemId);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [filmstripIsOpen, setFilmstripIsOpen] = useState(false);
  const [superSkuHoverId, setSuperSkuHoverId] = useState('');
  const [openTab, setOpenTab] = useState('Images');
  const [openImage, setOpenImage] = useState(null);

  useEffect(() => {
    if (hover) {
      window.LIFE_CYCLE_EVENT_BUS.on('supersku.hover', (hoverItemId) => {
        setSuperSkuHoverId(hoverItemId?.output);
      });
    }
  }, [hover]);

  useEffect(() => {
    setFilmstripIsOpen(showAvataarAR);
    setOpenTab('3D');
  }, [showAvataarAR]);

  const [superSkuHoveredProductRequest, superSkuHoveredProductResponse] = useLazyDataModel('product', {});

  useEffect(() => {
    if (superSkuHoverId) {
      superSkuHoveredProductRequest({
        variables: {
          itemId: superSkuHoverId,
          storeId,
          configId,
          skipPaintDetails: !configId
        }
      });
    }
  }, [superSkuHoverId]);

  useEffect(() => {
    componentReady({ itemId });
  }, [itemId]);

  const hasItemId = superSkuHoverId || itemId;
  const productData = useDataModel('product', {
    variables: {
      itemId,
      configId,
      skipPaintDetails: !configId
    },
    skip: !hasItemId
  });

  const reviewPhotos = useDataModel('reviewPhotos', {
    variables: { itemId: itemId || '' },
    skip: !itemId || skipReviewPhotos
  });

  const {
    externalMedia
  } = useLifeCycleEventBus('configurator.configurator_media_gallery_event');

  const { product = {} } = productData.data || {};
  const { brandName = '', productLabel = '' } = product.identifiers || {};

  // for swatch Hover
  const mainSuperSkuHover = Helper.getMainSuperSkuHover({
    itemId,
    superSkuHoveredProductResponse,
    showAvataarDefault,
    superSkuHoverId
  });

  const useFilmstripMobile = overlayType === 'filmstrip' && isMobile;

  const { displayedMedia = {}, ...apiResults } = Helper.getDisplayedMedia({
    isMobile,
    itemId,
    productData,
    externalMedia,
    overrideData,
    placeholder: showLoader && <MediaGalleryLoader isMobile isCarousel />,
    showAvataarDefault,
    pageType
  });

  useEffect(() => {
    const filtered = displayedMedia.main;
    const augmentedRealityIndex = displayedMedia.augmentedRealityIndex;

    let selectedMedia;
    if (filtered) {
      selectedMedia = filtered[showAvataarAR ? augmentedRealityIndex : selectedImageIndex];
    }

    let selectedTab;
    if (Helper.isImage({ media: selectedMedia })
      || Helper.isCustomColor({ media: selectedMedia })
      || Helper.isManualColor({ media: selectedMedia })
      || Helper.isHotspot({ media: selectedMedia })
    ) {
      selectedTab = 'Images';
    }
    if (Helper.isVideo({ media: selectedMedia })) {
      selectedTab = 'Videos';
    }
    if (Helper.isImage360({ media: selectedMedia })) {
      selectedTab = '360° View';
    }
    if (Helper.isAR({ media: selectedMedia }) && selectedMedia?.usdz.length === 0) {
      selectedTab = '3D';
    }

    if (selectedTab) {
      setOpenTab(selectedTab);
    }
  }, [selectedImageIndex, displayedMedia]);

  if (apiResults.apiState) {
    return apiResults.returnStatement;
  }
  const { thumbnails = [], main = [], zoom = [] } = displayedMedia || {};

  const carouselItemClassName = classNames(
    'media-gallery-carousel', {
      'filmstrip-mobile-carousel': useFilmstripMobile,
      'mobile-carousel': isMobile,
      'sui-relative': isMobile && FloatingActionButton
    }
  );

  const handleImageClick = (media, mediaIndex) => {
    if (useFilmstripMobile) {
      if (Helper.isImage({ media }) || Helper.isHotspot({ media })) {
        setOpenImage(media.url);
      }
      if (Helper.isVideo({ media })) {
        setOpenImage(media.videoStill);
      }
      if (Helper.isCustomColor({ media }) || Helper.isManualColor({ media })) {
        setOpenImage(media.id);
      }
      setFilmstripIsOpen(true);
    }

    // The UI state is updated first, which is critical for responsiveness
    setMainAsset(media);

    // The non-visual work is deferred off the critical interaction path for responsiveness
    const deferredWork = () => {
      // analytics
      // Trigger overlay open
      thumbnailClick({ primaryCategory: 'overlay', eventName: 'expanded-view' });
      // Image Info
      mediaClick({
        section: 'expanded-view',
        component: 'thumbnails',
        target: Helper.getMediaType(media),
        position: (mediaIndex + 1) || 'n/a',
        guid: Helper.getMediaType(media) === 'image' ? Helper.getImageGuid(media) : 'n/a'
      });

      // History mutation after analytics (browser-only)
      if (typeof globalThis.window !== 'undefined') {
        window.history.pushState({ page: 'MEDIAOVERLAY' }, null, '');
      }
    };

    // There may not be requestIdleCallback support in all environments where this component is used,
    // so we should fall back to a setTimeout if it's not available.
    // The timeout delay of 0 will still defer the work until after the current call stack is cleared,
    // which is sufficient for our purposes.
    if (typeof globalThis.window !== 'undefined' && typeof globalThis.window.requestIdleCallback === 'function') {
      globalThis.window.requestIdleCallback(deferredWork);
    } else if (typeof globalThis.window !== 'undefined') {
      setTimeout(deferredWork, 0);
    }

    if (Helper.isInternalAR(media) && onMediaClick) {
      onMediaClick({ media });
    }
  };

  const onChangeImage = () => {
    setTimeout(() => {
      const slides = carouselWrapperRef?.current?.querySelectorAll('.swiper-slide');
      const activeSlide = carouselWrapperRef?.current?.querySelector('.swiper-slide-active');
      const activeIndex = Array(...slides).indexOf(activeSlide);
      setSelectedImageIndex(activeIndex);
    }, 1);
  };

  const hideOverlay = () => {
    setMainAsset(null);
    setShowAvataarAR(false);
  };

  return (
    <div
      className={carouselItemClassName}
      ref={carouselWrapperRef}
      data-component="MediaGalleryCarousel"
    >
      {showFloatingActionButton && (
        <FloatingActionButton
          itemId={itemId || product?.itemId}
          isMobile={isMobile}
        />
      )}
      <Carousel
        pagination
        dynamicPagination={useFilmstripMobile || showDynamicPagination}
        peek={false}
        breakpoints={{
          sm: {
            slidesPerView: 1,
            slidesPerGroup: 1
          },
          md: {
            slidesPerView: 1,
            slidesPerGroup: 1
          },
          lg: {
            slidesPerView: 1,
            slidesPerGroup: 1
          },
          xl: {
            slidesPerView: 1,
            slidesPerGroup: 1
          }
        }}
        slidesPerView={1}
        onSlideChange={() => onChangeImage()}
      >
        {
          displayedMedia.main
            .map((media, mediaIndex) => {
              const {
                shortDescription,
                uploadDate,
                dateModified,
                url,
                mediaStill,
                videoStill,
                altText,
                rgb
              } = media;
              const isVideo = Helper.isVideo({ media });
              const isThreeSixty = Helper.isImage360({ media });
              const isImage = Helper.isImage({ media });
              const isAR = Helper.isAR({ media });
              const isAvataarAR = Helper.isAvataarAR({ media });
              const isHotspot = Helper.isHotspot({ media });
              const isCustomColor = Helper.isCustomColor({ media });
              const isManualColor = Helper.isManualColor({ media });
              let imageUrl = url;
              let index = 0;
              const alt = altText ?? (brandName && productLabel
                ? `${brandName} - ${productLabel}`
                : productLabel ?? '');

              if (mediaIndex === selectedImageIndex) {
                if (isImage) {
                  index = mainSuperSkuHover.findIndex((superSkuHoverMedia) => {
                    return superSkuHoverMedia.type === main[selectedImageIndex].type;
                  });
                  if (index > 0 && mainSuperSkuHover[index]) {
                    imageUrl = mainSuperSkuHover[index].url;
                  }
                  if (index <= 0 && mainSuperSkuHover[0]) {
                    imageUrl = mainSuperSkuHover[0].url;
                  }
                } else if (isVideo) {
                  imageUrl = mainSuperSkuHover[0] ? mainSuperSkuHover[0].url : videoStill;
                } else if (isThreeSixty || isAR) {
                  imageUrl = mainSuperSkuHover[0] ? mainSuperSkuHover[0].url : mediaStill;
                }
              } else if (isVideo) {
                imageUrl = videoStill;
              } else if (isThreeSixty || isAR) {
                imageUrl = mediaStill;
              }

              const videoScriptContent = JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'VideoObject',
                name: productLabel,
                description: shortDescription,
                thumbnailUrl: [videoStill],
                contentUrl: url,
                uploadDate,
                dateModified
              });

              return (
                <div
                  data-testid="carousel-slide"
                  className={classNames('media-gallery-carousel__slide', {
                    'active-slide': mediaIndex === selectedImageIndex
                  })}
                  key={mediaIndex}
                >
                  {isHotspot && (
                    <div className="hotspot-container" data-testid="hotspot-tag-icon">
                      <HotspotTagIcon
                        mediaGalleryIndex={mediaIndex}
                        openOverlay
                        hotspots={media?.hotspots}
                        ocrHotspots={media?.ocrHotspots}
                        imageUrl={imageUrl}
                        onHotspotImageClick={() => handleImageClick(media, mediaIndex)}
                      />
                    </div>
                  )}
                  {!isHotspot && (
                    <>
                      {isCustomColor || isManualColor
                        ? (
                          <>
                            {isManualColor
                              ? (
                                (
                                  <button
                                    type="button"
                                    className={classNames('manualSwatch',
                                      'sui-aspect-square',
                                      'sui-bg-inactive',
                                      'sui-flex',
                                      'sui-items-center',
                                      'sui-justify-center',
                                      'sui-h-full',
                                      'sui-mx-auto',
                                      'sui-border-0',
                                      'sui-p-0')}
                                    onClick={() => handleImageClick(media, mediaIndex)}
                                    data-testid="rgb-div"
                                  >
                                    <div className="sui-text-center sui-flex sui-items-center sui-justify-center">
                                      <div style={{ height: 'auto' }}>
                                        <PaintSwatches color="medium" />
                                        <div>No Color Preview Available</div>
                                      </div>
                                    </div>
                                    <div
                                      className="media-gallery-carousel__paint-reorder-color sui-absolute"
                                    >
                                      <Image
                                        alt={alt}
                                        src={media.brandLogo}
                                      />
                                    </div>
                                  </button>
                                )
                              )
                              : (
                                (
                                  // A button does not stretch like the block div it replaced, so it sizes itself.
                                  <button
                                    type="button"
                                    className={classNames('media-gallery-carousel__paint-reorder-color',
                                      'sui-w-full',
                                      'sui-h-full',
                                      'sui-border-0',
                                      'sui-p-0')}
                                    onClick={() => handleImageClick(media, mediaIndex)}
                                    data-testid="rgb-div"
                                    style={{
                                      backgroundColor: `rgb(${rgb.red},${rgb.green},${rgb.blue})`
                                    }}
                                  >
                                    <Image
                                      alt={alt}
                                      src={media.brandLogo}
                                    />
                                  </button>
                                )
                              )}
                          </>
                        )
                        : (
                          <>
                            <Image
                              className={classNames({
                                'video-thumbnail': isVideo,
                                image: isImage,
                                hotspot: isHotspot,
                                ar: isAR,
                                'three-sixty': isThreeSixty
                              })}
                              onClick={() => handleImageClick(media, mediaIndex)}
                              alt={alt}
                              src={imageUrl}
                              fetchPriority={mediaIndex === 0 ? firstItemFetchPriority : null}
                              lazy={mediaIndex >= 1}
                              decoding="async"
                              height={600}
                              width={600}
                            />
                            {!isImage
                              && (!mainSuperSkuHover[0] || mediaIndex !== selectedImageIndex)
                              && (
                                <button
                                  type="button"
                                  onClick={() => handleImageClick(media, mediaIndex)}
                                >
                                  <BackgroundCircleIcon />
                                  {isThreeSixty && <ThreeSixtyIcon />}
                                  {isAR && isAvataarAR && <ThreeSixtyIcon />}
                                  {isAR && !isAvataarAR && <ARIcon />}
                                  {isHotspot && <HotspotTagIcon />}
                                  {isVideo
                                    && (
                                      <>
                                        <PlayIcon />
                                        <script
                                          type="application/ld+json"
                                          /* eslint-disable-next-line react/no-danger */
                                          dangerouslySetInnerHTML={{ __html: videoScriptContent }}
                                        />
                                      </>
                                    )}
                                </button>
                              )}
                          </>
                        )}
                    </>
                  )}
                </div>
              );
            })
        }
      </Carousel>
      {((!!mainAsset || showAvataarAR) && !Helper.isInternalAR(mainAsset))
        && (
          <Suspense fallback={<div />}>
            {!useFilmstripMobile
              && ((!isMobile
                  && (
                    <MediaGalleryOverlay
                      handleClose={hideOverlay}
                      overlayOpened={!!mainAsset}
                      media={displayedMedia}
                      product={product}
                      mainAsset={mainAsset}
                      thumbnails={thumbnails}
                      itemId={itemId}
                      showAvataarDefault={showAvataarDefault}
                      showAvataarAR={showAvataarAR}
                    />
                  ))
                || (isMobile
                  && (
                    <MediaGalleryOverlayMobile
                      handleClose={hideOverlay}
                      overlayOpened={!!mainAsset}
                      itemId={itemId}
                      product={product}
                      mainImage={mainAsset || main[showAvataarAR ? (displayedMedia?.augmentedRealityIndex || 0) : 0]}
                      mainImages={main}
                      onMediaClick={handleImageClick}
                      zoomedImages={zoom}
                      thumbnails={thumbnails}
                      selectedImageIndex={selectedImageIndex}
                      showAvataarDefault={showAvataarDefault}
                      showAvataarAR={showAvataarAR}
                    />
                  )
                ))}
            {useFilmstripMobile
              && (
                <FilmstripMobile
                  data={{
                    displayedMedia: displayedMedia || { thumbnails: [], main: [], zoom: [] },
                    brandName: brandName || '',
                    galleryLabel: productLabel || ''
                  }}
                  isMobile
                  itemId={itemId}
                  isOpen={filmstripIsOpen}
                  setIsOpen={setFilmstripIsOpen}
                  openImage={openImage}
                  setOpenImage={setOpenImage}
                  selectedTab={openTab}
                  showAvataarDefault={showAvataarDefault}
                  showAvataarAR={showAvataarAR}
                  onAvataarButtonClick={() => setShowAvataarAR(false)}
                  reviewPhotos={reviewPhotos}
                />
              )}
          </Suspense>
        )}
    </div>
  );
};

MediaGalleryCarousel.displayName = 'MediaGalleryCarousel';

MediaGalleryCarousel.propTypes = {
  hover: bool,
  itemId: string.isRequired,
  configId: string,
  overlayType: oneOf([
    'default',
    'filmstrip' // filmstrip is currently only UX-approved for mobile
  ]),
  showDynamicPagination: bool,
  firstItemFetchPriority: oneOf([
    'high', // Fetch the image at a high priority relative to other images.
    'low', // Fetch the image at a low priority relative to other images.
    'auto' // Indicates no preference for the fetch priority. The browser decides what is best for the user.
  ]),
  showLoader: bool,
  onMediaClick: func,
  overrideData: shape({
    media: shape({}),
    galleryLabel: string,
    brandNameLabel: string
  }),
  pageType: string,
  skipReviewPhotos: bool
};

MediaGalleryCarousel.dataModel = dataModel;
